import { Skeleton } from "./components/ui/skeleton";
import { Dialog } from "./components/ui/dialog";
import { hasTranslation, translateKey, useTranslation } from "./i18n";
import { Button } from "./components/ui/button";
import { endpointProtocolLabel } from "./model-api-access";
import { catalogQueryPolicy, clearPrivateQueries, privateQueryKey, sensitiveQueryPolicy, sessionQueryPolicy } from "./query-policy";
import type { TaskModelSummary } from "@nexra/contracts";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { ApiError, api, dataApi } from "./api";

export function ConfirmActionDialog({
  title,
  description,
  confirmLabel,
  pending,
  destructive = false,
  alternativeAction,
  onClose,
  onConfirm,
}: Readonly<{
  title: string;
  description: string;
  confirmLabel: string;
  pending: boolean;
  destructive?: boolean;
  alternativeAction?: Readonly<{ label: string; pending: boolean; onAction(): void }>;
  onClose(): void;
  onConfirm(): void;
}>) {
  useTranslation();
  const busy = pending || Boolean(alternativeAction?.pending);
  return <Dialog title={title} pending={busy} onClose={onClose}><div className="panel-title"><h2>{title}</h2><button aria-label={translateKey("workspace.close")} className="icon-button" disabled={busy} onClick={onClose} type="button">×</button></div><p className="mb-0 mt-4 text-sm leading-6 text-zinc-600">{description}</p><div className="mt-6 flex justify-end gap-2"><Button variant="outline" disabled={busy} onClick={onClose} type="button">{translateKey("workspace.cancel")}</Button>{alternativeAction ? <Button variant="outline" aria-busy={alternativeAction.pending} disabled={busy} onClick={alternativeAction.onAction} type="button">{alternativeAction.label}</Button> : null}<Button variant={destructive ? "destructive" : "default"} aria-busy={pending} disabled={busy} onClick={onConfirm} type="button">{confirmLabel}</Button></div></Dialog>;
}

export function Metric({ label, value, loading = false }: Readonly<{ label: string; value: ReactNode; loading?: boolean }>) {
  return <div className="metric-card"><span>{label}</span>{loading ? <Skeleton className="h-8 w-20" /> : <strong>{value}</strong>}</div>;
}

export function useSession() {
  return useQuery({ ...sessionQueryPolicy, queryKey: ["session"], queryFn: ({ signal }) => dataApi.session(signal), retry: false });
}

/** Cookie-authenticated admin probe; shareable so login can start it before the session refetch lands. */
export async function probeAdminAccess(signal?: AbortSignal): Promise<boolean> {
  try {
    await api("admin/models?limit=1", { signal });
    return true;
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
    throw error;
  }
}

export function adminAccessQueryKey(userId: string | undefined) {
  return privateQueryKey("admin-access", userId);
}

export function useAdminAccess(userId: string | undefined) {
  return useQuery({
    queryKey: adminAccessQueryKey(userId),
    enabled: Boolean(userId),
    retry: false,
    ...sensitiveQueryPolicy,
    queryFn: ({ signal }) => probeAdminAccess(signal),
  });
}

export function prefetchAdminAccess(queryClient: QueryClient, userId: string) {
  void queryClient.prefetchQuery({
    queryKey: adminAccessQueryKey(userId),
    queryFn: ({ signal }) => probeAdminAccess(signal),
    ...sensitiveQueryPolicy,
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: () => api("api/auth/sign-out", { method: "POST", body: "{}" }),
    onMutate: () => clearPrivateQueries(queryClient),
    onSuccess: async () => {
      await clearPrivateQueries(queryClient);
      queryClient.removeQueries({ queryKey: ["session"] });
      window.location.assign("/");
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ["session"] }),
  });
}

export function useModels() {
  return useQuery({ ...catalogQueryPolicy, queryKey: ["models"], queryFn: ({ signal }) => dataApi.models(undefined, signal) });
}

export function useCatalogModels() {
  return useQuery({ ...catalogQueryPolicy, queryKey: ["models", "catalog"], queryFn: ({ signal }) => dataApi.catalogModels(signal) });
}

export function humanizeLabel(value: string): string {
  const labels: Record<string, string> = {
    language: "文本",
    text: "文本",
    image: "图片",
    video: "视频",
    audio: "音频",
    speech: "语音",
    embedding: "向量",
    reranker: "重排",
    world: "世界模型",
    multimodal: "多模态",
    generate: "生成",
    create: "创建",
    edit: "编辑",
    analyze: "分析",
    transform: "转换",
    text_to_image: "文生图",
    image_to_image: "图生图",
    text_to_video: "文生视频",
    image_to_video: "图生视频",
    audio_to_video: "音频生成视频",
    video_to_video: "视频转换",
    image_to_world: "图片生成世界",
    text_to_world: "文本生成世界",
    text_to_audio: "文生音频",
    text_to_music: "音乐生成",
    text_to_speech: "文字转语音",
    speech_to_text: "语音转文字",
    text_to_text: "文本生成",
    multimodal_to_text: "多模态转文本",
    text_embedding: "文本向量",
    image_embedding: "图片向量",
    video_embedding: "视频向量",
    voice_id: "音色 ID",
    output_format: "输出格式",
    audio_format: "音频格式",
    apply_text_normalization: "文本标准化",
    aigc_watermark: "AI 水印",
    with_audio: "生成音频",
    negative_prompt: "反向提示词",
    generate_count: "生成数量",
    reasoning_effort: "推理强度",
    reasoning: "推理",
    sse: "流式响应",
    streaming: "流式响应",
    tool_call: "工具调用",
    tool_calls: "工具调用",
  };
  const normalized = value.toLowerCase().replace(/[.\s-]+/g, "_");
  if (labels[normalized]) return labels[normalized];
  const operationKey = `operations.${normalized.replace(/_/g, "-")}`;
  if (hasTranslation(operationKey)) return translateKey(operationKey);
  return value.replace(/[._-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function catalogSummary(model: Pick<TaskModelSummary, "displayName" | "operation" | "summary">): string {
  return localizedCatalogText(model.summary, `${model.displayName}，支持${humanizeLabel(model.operation)}。`);
}

export function localizedCatalogText(value: string | null | undefined, fallback: string): string {
  return value && /[\u3400-\u9fff]/.test(value) ? value : fallback;
}

export function protocolLabel(protocol: string): string {
  return endpointProtocolLabel(protocol);
}
